import { MAX_MUTATIONS_PER_PUSH } from '@dovefiniscono/shared';
import type { SyncStore } from './store';
import type { SyncCoordinator, SyncTransport } from './types';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';

export type SyncStatus = {
  state: SyncState;
  /** Local changes not yet on the server. */
  pending: number;
  lastSyncedAt: number | null;
};

export type ErrorKind = 'offline' | 'unauthorized' | 'error';

/** Platform signals that make syncing worthwhile (browser: online event, tab visibility). */
export type AppLifecycle = {
  /** Calls back when the network or the app comes back; returns an unsubscribe function. */
  onResume(callback: () => void): () => void;
  isHidden(): boolean;
  /**
   * Calls back when someone uses the app (a click, a key, a scroll); returns an unsubscribe
   * function. Without it the app counts as always in use.
   */
  onActivity?(callback: () => void): () => void;
};

export type SyncEngineOptions = {
  store: SyncStore;
  transport: SyncTransport;
  coordinator?: SyncCoordinator;
  lifecycle?: AppLifecycle;
  /** Maps a transport failure to what the engine should do about it. */
  classifyError: (error: unknown) => ErrorKind;
  /** The session is gone: the app should sign out. */
  onUnauthorized?: () => void;
  /** Wait after a local write, so a burst of edits travels in one push. */
  debounceMs?: number;
  /** Background pull while the app is open and in use, to pick up changes from other devices. */
  intervalMs?: number;
  /**
   * Nobody has used the app for this long: background pulls stop until the next use, so the
   * server and its database can rest. Local changes still go out at once.
   */
  idleAfterMs?: number;
};

const MAX_BACKOFF_MS = 60_000;

/** Moves changes between the store and the API: pull, then push what is pending. */
export class SyncEngine {
  readonly #options: Required<
    Omit<SyncEngineOptions, 'coordinator' | 'lifecycle' | 'onUnauthorized'>
  > &
    Pick<SyncEngineOptions, 'coordinator' | 'lifecycle' | 'onUnauthorized'>;
  #status: SyncStatus;
  #statusListeners = new Set<() => void>();
  #running: Promise<void> | null = null;
  #again = false;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #failures = 0;
  #cleanup: (() => void)[] = [];

  constructor(options: SyncEngineOptions) {
    this.#options = { debounceMs: 800, intervalMs: 60_000, idleAfterMs: 3 * 60_000, ...options };
    this.#status = { state: 'idle', pending: options.store.pendingCount(), lastSyncedAt: null };
  }

  get status(): SyncStatus {
    return this.#status;
  }

  subscribeStatus = (listener: () => void): (() => void) => {
    this.#statusListeners.add(listener);
    return () => this.#statusListeners.delete(listener);
  };

  /** Syncs now. Concurrent calls share the run in progress and schedule one more after it. */
  sync(): Promise<void> {
    if (this.#running) {
      this.#again = true;
      return this.#running;
    }
    this.#running = this.#run().finally(() => {
      this.#running = null;
      if (this.#again) {
        this.#again = false;
        void this.sync();
      }
    });
    return this.#running;
  }

  /** Syncs after a short delay, collapsing repeated requests. */
  schedule(delayMs = this.#options.debounceMs): void {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#timer = null;
      void this.sync();
    }, delayMs);
  }

  /**
   * Starts automatic syncing: after local writes, periodically while the app is in use, on
   * reconnect, on focus, and at the first use after a pause.
   */
  start(): void {
    const { store, intervalMs, idleAfterMs, lifecycle } = this.#options;
    const hidden = () => lifecycle?.isHidden() ?? false;
    let lastUse = Date.now();
    const idle = () => lifecycle?.onActivity !== undefined && Date.now() - lastUse >= idleAfterMs;
    this.#cleanup.push(
      store.onChange(() => {
        this.#setStatus({ pending: store.pendingCount() });
        if (store.pendingCount() > 0) this.schedule();
      }),
    );
    const interval = setInterval(() => {
      if (!hidden() && !idle()) void this.sync();
    }, intervalMs);
    this.#cleanup.push(() => clearInterval(interval));
    if (lifecycle) {
      this.#cleanup.push(
        lifecycle.onResume(() => {
          lastUse = Date.now();
          void this.sync();
        }),
      );
    }
    if (lifecycle?.onActivity) {
      this.#cleanup.push(
        lifecycle.onActivity(() => {
          const wasIdle = idle();
          lastUse = Date.now();
          // Back after a pause: catch up at once instead of waiting for the next tick.
          if (wasIdle) void this.sync();
        }),
      );
    }
    void this.sync();
  }

  stop(): void {
    for (const cleanup of this.#cleanup.splice(0)) cleanup();
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
  }

  async #run(): Promise<void> {
    const { store, transport, coordinator } = this.#options;
    this.#setStatus({ state: 'syncing' });
    try {
      const exclusive = coordinator
        ? <T>(task: () => Promise<T>) => coordinator.exclusive(task)
        : <T>(task: () => Promise<T>) => task();
      await exclusive(async () => {
        store.applyPull(await transport.pull(store.cursor));

        for (;;) {
          const { mutations, sent } = store.pendingMutations(MAX_MUTATIONS_PER_PUSH);
          if (mutations.length === 0) break;
          const cursorBefore = store.cursor;
          const response = await transport.push(mutations);
          const { refetch } = store.acknowledge(sent, response);
          if (refetch) store.resetCursor();
          // Nobody else wrote in between: the new version holds only our own changes.
          else if (response.version === cursorBefore + 1) store.advanceCursor(response.version);
          if (mutations.length < MAX_MUTATIONS_PER_PUSH) break;
        }
      });
      this.#failures = 0;
      this.#setStatus({ state: 'idle', lastSyncedAt: Date.now(), pending: store.pendingCount() });
    } catch (error) {
      const kind = this.#options.classifyError(error);
      if (kind === 'unauthorized') {
        this.#setStatus({ state: 'error' });
        this.#options.onUnauthorized?.();
        return;
      }
      this.#failures++;
      this.#setStatus({ state: kind === 'offline' ? 'offline' : 'error' });
      // Retry with exponential backoff: 2s, 4s, 8s… up to a minute.
      this.schedule(Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.#failures));
    }
  }

  #setStatus(patch: Partial<SyncStatus>): void {
    this.#status = { ...this.#status, ...patch };
    for (const listener of this.#statusListeners) listener();
  }
}
